import { createAccessControl } from 'better-auth/plugins/access'
import { defaultStatements, memberAc, ownerAc } from 'better-auth/plugins/organization/access'
import { z } from 'zod'

/** Workspaces are Better Auth organizations. These are the product-facing roles. */
export const workspaceRoles = ['owner', 'editor', 'viewer'] as const
export type WorkspaceRole = (typeof workspaceRoles)[number]

export const workspaceRoleLabels: Record<WorkspaceRole, string> = {
  owner: 'Vlastník',
  editor: 'Editor',
  viewer: 'Jen čtení',
}

export const workspaceAccessControl = createAccessControl(defaultStatements)

const workspaceOwnerRole = workspaceAccessControl.newRole({
  ...ownerAc.statements,
})

const workspaceEditorRole = workspaceAccessControl.newRole({
  ...memberAc.statements,
})

const workspaceViewerRole = workspaceAccessControl.newRole({
  ...memberAc.statements,
})

export const workspaceOrganizationRoles = {
  owner: workspaceOwnerRole,
  editor: workspaceEditorRole,
  viewer: workspaceViewerRole,
  // Historical Better Auth defaults may still exist in old data or raw auth calls.
  admin: workspaceEditorRole,
  member: workspaceViewerRole,
} as const

export function isWorkspaceRole(value: unknown): value is WorkspaceRole {
  return typeof value === 'string' && workspaceRoles.includes(value as WorkspaceRole)
}

export function normalizeWorkspaceRole(role: string | null | undefined): WorkspaceRole {
  const roles = role
    ?.split(',')
    .map((item) => item.trim())
    .filter(Boolean)

  if (roles?.includes('owner')) {
    return 'owner'
  }

  if (roles?.some((item) => item === 'editor' || item === 'admin')) {
    return 'editor'
  }

  return 'viewer'
}

export function canWriteWorkspace(role: WorkspaceRole) {
  return role === 'owner' || role === 'editor'
}

export function canManageWorkspaceMembers(role: WorkspaceRole) {
  return role === 'owner'
}

export const workspaceSlugSchema = z
  .string()
  .min(3, 'Use at least 3 characters')
  .max(48, 'Use at most 48 characters')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and dashes')

export const createWorkspaceSchema = z.object({
  name: z.string().trim().min(1, 'Enter a workspace name').max(100),
  slug: workspaceSlugSchema,
})

export const updateWeddingIdentitySchema = z.object({
  brideFirstName: z.string().trim().min(1, 'Enter the bride first name').max(100),
  brideLastName: z.string().trim().max(100).optional(),
  groomFirstName: z.string().trim().min(1, 'Enter the groom first name').max(100),
  groomLastName: z.string().trim().max(100).optional(),
})

export const updateWorkspaceSlugSchema = z.object({
  slug: workspaceSlugSchema,
})

export const inviteMemberSchema = z.object({
  email: z.email('Enter a valid email address'),
  role: z.enum(workspaceRoles),
})

export const updateWorkspaceMemberRoleSchema = z.object({
  role: z.enum(workspaceRoles),
})

export const workspaceInvitationStatuses = ['pending', 'accepted', 'rejected', 'canceled'] as const
export type WorkspaceInvitationStatus = (typeof workspaceInvitationStatuses)[number]

export type WorkspaceMemberDto = {
  memberId: string
  userId: string
  name: string
  email: string
  image: string | null
  role: WorkspaceRole
  createdAt: string
}

export type WorkspaceInvitationDto = {
  id: string
  email: string
  role: WorkspaceRole
  status: WorkspaceInvitationStatus
  expiresAt: string
  createdAt: string
  inviter: { id: string; name: string; email: string } | null
  inviteUrl?: string
}

export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>
export type UpdateWeddingIdentityInput = z.infer<typeof updateWeddingIdentitySchema>
export type UpdateWorkspaceSlugInput = z.infer<typeof updateWorkspaceSlugSchema>
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>
export type UpdateWorkspaceMemberRoleInput = z.infer<typeof updateWorkspaceMemberRoleSchema>
